import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { RequestsFormValues } from '../../models';
import { Colors } from '../../../../../assets';
import { Spacing } from '../../../../../assets/GlobalStyle/Variables';

type Field<T> = { key: keyof T; label: string };

type BloodClottedFields = RequestsFormValues['biochemistry']['form_data']['blood_clotted'];
type BloodUnclottedFields = RequestsFormValues['biochemistry']['form_data']['blood_unclotted'];
type BloodK3EdtaFields = RequestsFormValues['biochemistry']['form_data']['blood_k3edta'];
type BloodNafEdtaFields = RequestsFormValues['biochemistry']['form_data']['blood_naf_edta'];
type BloodCitrateFields = RequestsFormValues['biochemistry']['form_data']['blood_citrate'];
type BloodClottedSeparateFields = RequestsFormValues['biochemistry']['form_data']['blood_clotted_separate'];
type UrineSingleFields = RequestsFormValues['biochemistry']['form_data']['urine_single'];

const BLOOD_CLOTTED: Field<BloodClottedFields>[] = [
  { key: 'natrium', label: 'Natrium (Na)' },
  { key: 'kalium', label: 'Kalium (K)' },
  { key: 'chlorides', label: 'Chloridy' },
  { key: 'calcium_total', label: 'Kalcium celkové' },
  { key: 'phosphorus', label: 'Fosfor' },
  { key: 'magnesium', label: 'Magnézium' },
  { key: 'glucose', label: 'Glukóza' },
  { key: 'urea', label: 'Urea' },
  { key: 'creatinine', label: 'Kreatinin' },
  { key: 'uric_acid', label: 'Kyselina močová' },
  { key: 'bilirubin', label: 'Bilirubin celkový' },
  { key: 'bilirubin_conjugated', label: 'Bilirubin konjugovaný' },
  { key: 'ast', label: 'AST' },
  { key: 'alt', label: 'ALT' },
  { key: 'alp', label: 'ALP' },
  { key: 'ggt', label: 'GGT' },
  { key: 'ck', label: 'CK' },
  { key: 'ldh', label: 'LDH' },
  { key: 'ches', label: 'ChE' },
  { key: 'ams', label: 'Amyláza (AMS)' },
  { key: 'lipase', label: 'Lipáza' },
  { key: 'bile_acids', label: 'Žlučové kyseliny' },
  { key: 'ace', label: 'ACE' },
  { key: 'osmolality', label: 'Osmolalita' },
  { key: 'cystatin_c', label: 'Cystatin C' },
  { key: 'crp', label: 'CRP' },
  { key: 'cholesterol', label: 'Cholesterol' },
  { key: 'hdl_cholesterol', label: 'HDL cholesterol' },
  { key: 'ldl_cholesterol', label: 'LDL cholesterol' },
  { key: 'triacylglycerols', label: 'Triacylglyceroly' },
  { key: 'apolipoprotein_a1', label: 'Apolipoprotein A1' },
  { key: 'apolipoprotein_b', label: 'Apolipoprotein B' },
  { key: 'lipoprotein_a', label: 'Lipoprotein (a)' },
  { key: 'total_protein', label: 'Celková bílkovina' },
  { key: 'albumin', label: 'Albumin' },
  { key: 'beta_2_microglobulin', label: 'Beta-2 mikroglobulin' },
  { key: 'prealbumin', label: 'Prealbumin' },
  { key: 'transferrin', label: 'Transferin' },
  { key: 'transferrin_saturation', label: 'Saturace transferinu' },
  { key: 'ferritin', label: 'Ferritin' },
  { key: 'iron', label: 'Železo' },
  { key: 'iron_binding_capacity', label: 'Vazebná kapacita Fe' },
  { key: 'vitamin_b12', label: 'Vitamin B12' },
  { key: 'vitamin_b12_active', label: 'Vitamin B12 aktivní' },
  { key: 'folate', label: 'Folát' },
  { key: 'hs_troponin_i', label: 'hs-Troponin I' },
  { key: 'myoglobin', label: 'Myoglobin' },
  { key: 'nt_probnp', label: 'NT-proBNP' },
  { key: 'digoxin', label: 'Digoxin' },
  { key: 'theophylline', label: 'Teofylin' },
  { key: 'afp', label: 'AFP' },
  { key: 'cea', label: 'CEA' },
  { key: 'ca_15_3', label: 'CA 15-3' },
  { key: 'ca_19_9', label: 'CA 19-9' },
  { key: 'ca_125', label: 'CA 125' },
  { key: 'ca_72_4', label: 'CA 72-4' },
  { key: 'hcg_beta', label: 'HCG beta' },
  { key: 'psa_total', label: 'PSA celkový' },
  { key: 'psa_free', label: 'PSA volný' },
  { key: 'scca', label: 'SCC-Ag' },
  { key: 's100b', label: 'S100B' },
  { key: 'nse', label: 'NSE' },
  { key: 'tsh', label: 'TSH' },
  { key: 't4_total', label: 'T4 celkový' },
  { key: 't3_total', label: 'T3 celkový' },
  { key: 't4_free', label: 'FT4' },
  { key: 't3_free', label: 'FT3' },
  { key: 'anti_tg', label: 'Anti-TG' },
  { key: 'anti_tpo', label: 'Anti-TPO' },
  { key: 'anti_tsh_receptor_trak', label: 'Anti-TSH rec. (TRAK)' },
  { key: 'cortisol', label: 'Kortizol' },
  { key: 'estradiol', label: 'Estradiol' },
  { key: 'progesterone', label: 'Progesteron' },
  { key: 'prolactin', label: 'Prolaktin' },
  { key: 'lh', label: 'LH' },
  { key: 'fsh', label: 'FSH' },
  { key: 'testosterone', label: 'Testosteron' },
  { key: 'dhea_s', label: 'DHEA-S' },
  { key: 'shbg', label: 'SHBG' },
  { key: 'c_peptide', label: 'C-peptid' },
  { key: 'vitamin_d_25_oh', label: 'Vitamin D (25-OH)' },
  { key: 'phenytoin', label: 'Fenytoin' },
  { key: 'valproic_acid', label: 'Kyselina valproová' },
  { key: 'lithium', label: 'Lithium' },
  { key: 'interleukin_6', label: 'Interleukin 6 (IL-6)' },
  { key: 'procalcitonin_pct', label: 'Prokalcitonin (PCT)' },
  { key: 'plgf', label: 'PlGF' },
  { key: 'igf_1', label: 'IGF-1' }
];

const BLOOD_UNCLOTTED: Field<BloodUnclottedFields>[] = [
  { key: 'blood_count', label: 'Krevní obraz' },
  { key: 'blood_count_diff_analyzer', label: 'KO diff. analyzátor' },
  { key: 'blood_count_reticulocytes', label: 'KO + retikulocyty' },
  { key: 'blood_count_diff_reticulocytes', label: 'KO diff. + retikulocyty' },
  { key: 'immature_platelet_fraction', label: 'Nezralé trombocyty (IPF)' },
  { key: 'differential_microscopy', label: 'Diff. mikroskopie' },
  { key: 'platelets_microscopy', label: 'Trombocyty mikroskopie' },
  { key: 'schistocytes', label: 'Schistocyty' }
];

const BLOOD_K3EDTA: Field<BloodK3EdtaFields>[] = [
  { key: 'hba1c', label: 'HbA1c' },
  { key: 'homocysteine', label: 'Homocystein' },
  { key: 'parathormone', label: 'Parathormon (PTH)' }
];

const BLOOD_NAF_EDTA: Field<BloodNafEdtaFields>[] = [
  { key: 'glucose', label: 'Glukóza' },
  { key: 'ogtt', label: 'oGTT' },
  { key: 'ogtt_pregnant', label: 'oGTT těhotné' },
  { key: 'lactate', label: 'Laktát' }
];

const BLOOD_CITRATE_BOOL: Field<BloodCitrateFields>[] = [
  { key: 'prothrombin_test_quick', label: 'Protrombinový čas (Quick)' },
  { key: 'aptt', label: 'APTT' },
  { key: 'thrombin_test', label: 'Trombinový test' },
  { key: 'd_dimers', label: 'D-dimery' },
  { key: 'fibrinogen', label: 'Fibrinogen' },
  { key: 'antithrombin', label: 'Antitrombin' },
  { key: 'fdp', label: 'FDP' },
  { key: 'fm', label: 'FM (fibrinový monomer)' },
  { key: 'protein_c', label: 'Protein C' },
  { key: 'protein_s', label: 'Protein S' },
  { key: 'apc_resistance', label: 'APC rezistence' },
  { key: 'factor_viii', label: 'Faktor VIII' },
  { key: 'factor_ix', label: 'Faktor IX' },
  { key: 'factor_xii', label: 'Faktor XII' },
  { key: 'lupus_anticoagulans', label: 'Lupus antikoagulans' },
  { key: 'anti_xa_lmwh', label: 'Anti-Xa LMWH' },
  { key: 'anti_xa_ufh', label: 'Anti-Xa UFH' },
  { key: 'arixtra', label: 'Arixtra' },
  { key: 'apixaban', label: 'Apixaban' },
  { key: 'rivaroxaban', label: 'Rivaroxaban' },
  { key: 'acetylsalicylic_acid_asa', label: 'Kyselina acetylsalicylová (ASA)' },
  { key: 'clopidogrel_prasugrel_ticlopidine_ticagrelor', label: 'Clopidogrel / Prasugrel / Tiklopidin / Tikagrel' }
];

const BLOOD_CLOTTED_SEPARATE: Field<BloodClottedSeparateFields>[] = [
  { key: 'blood_group_rh_factor', label: 'Krevní skupina + Rh faktor' },
  { key: 'antibody_screening', label: 'Screening protilátek' },
  { key: 'direct_coombs_test', label: 'Přímý Coombsův test' }
];

const URINE_SINGLE: Field<UrineSingleFields>[] = [
  { key: 'urine_chemistry_morphology', label: 'Chemie + morfologie moče' },
  { key: 'amylase', label: 'Amyláza' },
  { key: 'cb_creatinine', label: 'CB + kreatinin' },
  { key: 'albumin_creatinine', label: 'Albumin + kreatinin' },
  { key: 'osmolality', label: 'Osmolalita' },
  { key: 'beta_2_microglobulin', label: 'Beta-2 mikroglobulin' }
];

export const BiochemistryForm: FC = () => {
  const { register } = useFormContext<RequestsFormValues>();

  return (
    <Wrapper>
      {/* Primární vzorek */}
      <Section>
        <Row>
          <Col md={3}><SectionLabel>Primární vzorek</SectionLabel></Col>
          <Col md={9}>
            <TextInput type="text" placeholder="popis vzorku" {...register('biochemistry.primary_sample')} />
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Krev srážlivá */}
      <Section>
        <Row>
          <Col md={3}><SectionLabel>Krev srážlivá</SectionLabel></Col>
          <Col md={9}>
            <CheckGrid>
              {BLOOD_CLOTTED.map((f) => (
                <Check key={f.key as string}>
                  <input type="checkbox" {...register(`biochemistry.form_data.blood_clotted.${f.key as string}` as any)} />
                  <span>{f.label}</span>
                </Check>
              ))}
            </CheckGrid>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Krev nesrážlivá (EDTA) */}
      <Section>
        <Row>
          <Col md={3}><SectionLabel>Krev nesrážlivá (EDTA)</SectionLabel></Col>
          <Col md={9}>
            <CheckGrid>
              {BLOOD_UNCLOTTED.map((f) => (
                <Check key={f.key as string}>
                  <input type="checkbox" {...register(`biochemistry.form_data.blood_unclotted.${f.key as string}` as any)} />
                  <span>{f.label}</span>
                </Check>
              ))}
            </CheckGrid>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Krev K3EDTA */}
      <Section>
        <Row>
          <Col md={3}><SectionLabel>Krev K3EDTA</SectionLabel></Col>
          <Col md={9}>
            <CheckGrid>
              {BLOOD_K3EDTA.map((f) => (
                <Check key={f.key as string}>
                  <input type="checkbox" {...register(`biochemistry.form_data.blood_k3edta.${f.key as string}` as any)} />
                  <span>{f.label}</span>
                </Check>
              ))}
            </CheckGrid>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Krev NaF+EDTA */}
      <Section>
        <Row>
          <Col md={3}><SectionLabel>Krev NaF+EDTA</SectionLabel></Col>
          <Col md={9}>
            <CheckGrid>
              {BLOOD_NAF_EDTA.map((f) => (
                <Check key={f.key as string}>
                  <input type="checkbox" {...register(`biochemistry.form_data.blood_naf_edta.${f.key as string}` as any)} />
                  <span>{f.label}</span>
                </Check>
              ))}
            </CheckGrid>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Krev citrát */}
      <Section>
        <Row>
          <Col md={3}><SectionLabel>Krev citrát</SectionLabel></Col>
          <Col md={9}>
            <CheckGrid>
              {BLOOD_CITRATE_BOOL.map((f) => (
                <Check key={f.key as string}>
                  <input type="checkbox" {...register(`biochemistry.form_data.blood_citrate.${f.key as string}` as any)} />
                  <span>{f.label}</span>
                </Check>
              ))}
            </CheckGrid>
            <AnticoagRow>
              <AnticoagLabel>Antikoagulační léčba</AnticoagLabel>
              <TextInput
                type="text"
                placeholder="upřesnění"
                {...register('biochemistry.form_data.blood_citrate.anticoagulation_treatment')}
              />
            </AnticoagRow>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Krev srážlivá separát */}
      <Section>
        <Row>
          <Col md={3}><SectionLabel>Krev srážlivá separát</SectionLabel></Col>
          <Col md={9}>
            <CheckGrid>
              {BLOOD_CLOTTED_SEPARATE.map((f) => (
                <Check key={f.key as string}>
                  <input type="checkbox" {...register(`biochemistry.form_data.blood_clotted_separate.${f.key as string}` as any)} />
                  <span>{f.label}</span>
                </Check>
              ))}
            </CheckGrid>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Moč jednorázová */}
      <Section>
        <Row>
          <Col md={3}><SectionLabel>Moč jednorázová</SectionLabel></Col>
          <Col md={9}>
            <CheckGrid>
              {URINE_SINGLE.map((f) => (
                <Check key={f.key as string}>
                  <input type="checkbox" {...register(`biochemistry.form_data.urine_single.${f.key as string}` as any)} />
                  <span>{f.label}</span>
                </Check>
              ))}
            </CheckGrid>
          </Col>
        </Row>
      </Section>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  padding: ${Spacing(3)} 0;
`;

const SectionLabel = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${Colors.lightGrey};
  margin: 0;
`;

const TextInput = styled.input`
  height: 34px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
  max-width: 400px;
`;

const CheckGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${Spacing(2)};
`;

const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  user-select: none;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  span {
    color: ${Colors.grey};
  }
`;

const AnticoagRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing(3)};
  margin-top: ${Spacing(3)};
`;

const AnticoagLabel = styled.div`
  color: ${Colors.grey};
  white-space: nowrap;
  min-width: 180px;
`;
